import moment from "moment";
import Bugsnag from "@bugsnag/js";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import { messages } from "./messages";
import { toast } from "react-toastify";
import { companyConfig } from "./constants";
import { isDesktop } from "react-device-detect";
import dataURLtoBlob from "blueimp-canvas-to-blob";

const toastList = new Set();
const MAX_TOAST = 2;
export const API_TIME_OUT = 200;
export const notify = (test, message) => {
  if (toastList.size < MAX_TOAST) {
    let id;
    if (message === "success") {
      id = toast.success(test, {
        autoClose: 1500,
        onClose: () => toastList.delete(id),
      });
    } else if (message === "warning") {
      id = toast.warning(test, {
        autoClose: 1500,
        onClose: () => toastList.delete(id),
      });
    } else {
      id = toast.error(test, {
        autoClose: 1500,
        onClose: () => toastList.delete(id),
      });
    }
    toastList.add(id);
  }
};
export const notifyAPIMessages = (res) => {
  if (res) {
    if (res?.status === 200 || res?.status === 201) {
      notify(res?.data?.message, "success");
    } else {
      const newMessage =
        res?.response?.data?.message || res?.response?.data || res?.message;
      newMessage && notify(newMessage);
    }
  }
};

const moment2 = require("moment-timezone");
export const defaultTimeZone = moment2.tz.guess();
export const DateUtility = {
  mmddyydashFormat: (date) =>
    moment
      .utc(date, "YYYYMMDD HH:mm:ss")
      .clone()
      .tz(defaultTimeZone)
      .format("MM-DD-YY"),
  mmddyyWithHHMM: (date) =>
    moment
      .utc(date, "YYYYMMDD HH:mm:ss")
      .clone()
      .tz(defaultTimeZone)
      .format("MM/DD/YY hh:mm A"),
  mmddyywithDay: (date) => moment(date || new Date()).format("MM/DD/YY - ddd"),
  dashDate: (date) => moment(date).format("MM-DD-YYYY"),
  dateNTime: (date) => moment(date).format("hh:mm A, DD MMM YYYY"),
  ancDuration: (date) => moment(date).format("DD MMM YY"),
  todayDay: (date) => moment(date).weekday(),
  todayDate: (date) => moment(date).format("DD"),
  month: (date) => moment(date || new Date()).format("MMM"),
  monthYear: (date) => moment(date).format("MMM YYYY"),
  date24time: (date) => moment(date).format("ddd, MMM D, YYYY. h:mm"),
  AmPmTime: (date) => moment(date, "h:mm:ss A").format("LT"),
  nameDayMonthYear: (date) => moment(date).format("DD MMM YYYY"),
  dayMonthYearTime: (date) => moment(date).format("DD MMMM YYYY, hh:mm A"),
  monthDateYearTime: (date) => moment(date).format("MMMM DD, YYYY, HH:mm"),
  dayMonthYearTimeWithDash: (date) =>
    date ? moment(date).format("DD-MM-YYYY, hh:mm A") : " - ",
  dayMonthYearTimeWithSlash: (date) =>
    date ? moment(date).format("DD/MM/YYYY, hh:mm A") : " - ",
  AmPmTimeWithoutSecond: (date) => moment(date).format("hh:mm A"),
  nameDayMonthYearTime: (date) =>
    moment(date).format("ddd, DD MMM YYYY, hh:mm A"),
  dayMonthYear: (date) => moment(date).format("DD MMM YYYY"),
  requestDateTime: (date) => moment(date).format("YYYY-MM-DD HH:mm:ss"),
  dayMonth: (date) => moment(date).format("DD MMM"),
  fullDateTime: (date) => moment(date).format("MMMM DD, YYYY, hh:mm A"),
  requestTime: (date) => moment(date).format("HH:mm:ss"),
  orderTime: (date) => moment(date).format("DD MMM, hh:mm A"),
  dayMonthYear2: (date) => moment(date).format("DD/MM/YYYY"),
};

export const dateFormatting = (date) => {
  return moment(date || new Date()).format("YYYY-MM-DD");
};

export const dateFormatting2 = (date) => {
  return moment(date).format("MM-DD-YYYY");
};

export const dateFormatWithTime = (date) => {
  return moment(date).format("YYYY-MM-DD HH:mm:ss");
};

export const dateFormatDateWithTime = (date) => {
  return moment
    .utc(date)
    .clone()
    .tz(defaultTimeZone)
    .format("MM/DD/YY HH:mm A");
};

export const getMonthStartEndDate = (month) => {
  const start = moment()
    .add(month ? month : 0, "months")
    .startOf("month")
    .format();
  const end = moment()
    .add(month ? month : 0, "months")
    .endOf("month")
    .format();
  const year = moment()
    .clone()
    .add(month ? month : 0, "month")
    .format("YYYY");
  const cMonth = moment()
    .clone()
    .add(month ? month : 0, "month")
    .format("MMMM");
  return {
    start: new Date(start),
    end: new Date(end),
    year: Number(year),
    month: cMonth,
  };
};

export const getCurrNextMonthDetails = () => {
  const currDate = new Date();
  const currMonth = currDate.getMonth() + 1;
  const currYear = currDate.getFullYear();
  const currentMonth = {
    value: moment().format("M"),
    label: moment().format("MMMM"),
  };
  const nextMonth = {
    value: moment().add(1, "months").format("M"),
    label: moment().add(1, "months").format("MMMM"),
  };
  const selectedMonth = Number(nextMonth.value);
  const reqObj = {
    cMName: currentMonth.label,
    cMNumber: Number(currentMonth.value),
    nMName: nextMonth.label,
    nMNumber: Number(nextMonth.value),
    cMYear: currYear,
    nMYear: currMonth > selectedMonth ? currYear + 1 : currYear,
  };
  return reqObj;
};

export const getTimeDifference = (date1, date2) => {
  const ndate1 = new Date(date1);
  const ndate2 = new Date(date2);
  const milliseconds = Math.abs(ndate2 - ndate1);
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(milliseconds / 1000 / 60);
  return { milliseconds, seconds, minutes };
};

export const getLocationCoordinates = async (isCache, checkError = false) => {
  if (!navigator.geolocation) {
    notify(messages?.errors?.location_not_support);
    return null;
  }

  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        (error) => {
          const errorMessages = {
            [error.PERMISSION_DENIED]: {
              title: "Permission Denied",
              message: messages.errors.location_permission_issue,
            },
            [error.POSITION_UNAVAILABLE]: {
              title: "Position Unavailable",
              message: messages.errors.location_position_issue,
            },
            [error.TIMEOUT]: {
              title: "Request Timeout",
              message: messages.errors.location_timeout_issue,
            },
          };

          const errorDetails = errorMessages[error.code] || {
            title: "Location Unknown Issue",
            message: messages.errors.location_unknown_issue,
          };

          const reqObj = {
            date: new Date(),
            code: error.code,
            error: error.message,
            ...errorDetails,
          };
          reject(reqObj);
        },
        {
          enableHighAccuracy: true,
          timeout: isCache ? 30000 : !checkIsEnable()?.isTimeIn ? 10000 : 2000,
          maximumAge: isCache ? 30000 : 0,
        }
      );
    });
    return position.coords;
  } catch (error) {
    manageErrorLogs(error);
    if (checkError) {
      throw error;
    } else {
      return null;
    }
  }
};

export const checkSystemWebcam = () => {
  return new Promise((resolve, reject) => {
    if (isDesktop) {
      navigator.mediaDevices
        .getUserMedia({ video: { width: 300 } })
        .then((stream) => {
          const tracks = stream.getTracks();
          tracks.forEach((track) => track.stop());
          resolve(stream);
        })
        .catch((error) => {
          manageErrorLogs(error);
          if (error.name === "NotAllowedError") {
            notify(messages.errors.camera_permission, "error");
          } else {
            notify(messages.errors.camera_not_support, "error");
          }
          reject(error);
        });
    } else {
      resolve();
    }
  });
};

export const checkSystemMicrophone = () => {
  return new Promise((resolve, reject) => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
        resolve(stream);
      })
      .catch(async (error) => {
        manageErrorLogs(error);
        if (error.name === "NotAllowedError") {
          notify(messages.errors.microphone_permission, "error");
        } else {
          notify(messages.errors.microphone_not_support, "error");
        }
        reject(error);
      });
  });
};

export const getCameraStream = async (webcamRef, onChange) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: 800, height: 800, facingMode: "user" },
    });
    webcamRef.current.srcObject = stream;
    onChange(stream);
  } catch (error) {
    manageErrorLogs(error);
    notify("Unable to access the front camera. Switching to the back camera.");
    try {
      const fallbackStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 800, height: 800, facingMode: "environment" },
      });
      webcamRef.current.srcObject = fallbackStream;
    } catch (error2) {
      manageErrorLogs(error2);
      notify("Unable to access any camera.");
    }
  }
};

export const downloadDataAsExcel = (dataList, nameOfData, fileName) => {
  return new Promise((resolve, reject) => {
    try {
      const workbook = XLSX.utils.book_new();
      if (dataList && dataList?.length === 0) resolve();
      dataList.forEach((data, index) => {
        let sheet = XLSX.utils.aoa_to_sheet(data);
        sheet["!cols"] = fitToColumn(data);
        XLSX.utils.book_append_sheet(workbook, sheet, nameOfData[index]);
      });
      function fitToColumn(arrayOfArray) {
        return arrayOfArray[0].map((_, i) => ({
          wch: Math.max(
            ...arrayOfArray.map((row) =>
              row[i] ? row[i].toString().length : 0
            )
          ),
        }));
      }
      XLSX.writeFile(workbook, fileName);
      resolve();
    } catch (error) {
      notify("Failed to download excel file!", "error");
      reject(error);
    }
  });
};
export const downloadDataAsPDF = async (
  columns,
  rows,
  fileName,
  imagesArray = [],
  boxSize = 20,
  colFontSize = 4
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      const processedRows = [];
      for (const row of rows) {
        try {
          const newRow = Array.isArray(row) ? [...row] : Object.values(row);
          for (const imgCol of imagesArray) {
            const imgData = newRow[imgCol];
            if (imgData && typeof imgData === "string") {
              newRow[imgCol] = await fixImageOrientation(imgData);
            }
          }
          processedRows.push(newRow);
        } catch (error) {
          console.error("Error processing row:", error);
          processedRows.push(["Error processing this row"]);
        }
      }
      const pageWidth = 297;
      const marginLeft = 5;
      const marginRight = 5;
      const usableWidth = pageWidth - marginLeft - marginRight;
      const numberOfColumns = columns.length;
      const columnWidth = usableWidth / numberOfColumns;
      const columnStyles = {};
      for (let i = 0; i < numberOfColumns; i++) {
        columnStyles[i] = {
          cellWidth: columnWidth,
          halign: "left",
        };
      }
      imagesArray.forEach((index) => {
        columnStyles[index] = {
          cellWidth: columnWidth,
          halign: "center",
        };
      });
      autoTable(doc, {
        columns,
        body: processedRows,
        margin: {
          top: 30,
          bottom: 30,
          left: marginLeft,
          right: marginRight,
        },
        theme: "grid",
        styles: {
          fontSize: colFontSize,
          cellPadding: 1,
          lineHeightFactor: 1,
          minCellHeight: boxSize,
        },
        headStyles: {
          fillColor: [22, 160, 133],
          textColor: 255,
          fontSize: colFontSize,
          halign: "left",
          minCellHeight: 8,
        },
        columnStyles,
        rowPageBreak: "avoid",

        didParseCell: (data) => {
          const colIdx = data.column.index;
          const colWidth = columnStyles[colIdx]?.cellWidth || columnWidth;
          if (imagesArray.includes(colIdx) && data.row.section === "body") {
            data.cell.text = "";
            data.cell.styles.minCellHeight = boxSize;
            data.cell.styles.cellPadding = {
              top: 1,
              right: 1,
              bottom: 1,
              left: 1,
            };
            return;
          }

          if (
            data.row.section === "body" &&
            (typeof data.cell.raw === "string" ||
              typeof data.cell.raw === "number")
          ) {
            const text = data.cell.raw;
            const lines = doc.splitTextToSize(text, colWidth);
            data.cell.text = lines;
          }
        },

        didDrawCell: (data) => {
          if (
            imagesArray.includes(data.column.index) &&
            data.row.section === "body"
          ) {
            const imageData = data.cell.raw;
            if (
              imageData &&
              typeof imageData === "string" &&
              imageData.startsWith("data:image/")
            ) {
              try {
                doc.addImage(
                  imageData,
                  "JPEG",
                  data.cell.x + 1,
                  data.cell.y + 1,
                  columnStyles[data.column.index].cellWidth - 2,
                  boxSize - 2
                );
              } catch (error) {
                console.error("Error drawing image:", error);
              }
            }
          }
        },
      });
      doc.setFontSize(4);
      doc.save(fileName);
      resolve();
    } catch (error) {
      console.error("PDF Generation Error:", error);
      notify(error.message || "Failed to download PDF file!");
      reject(error);
    }
  });
};

export const fixImageOrientation = async (imageURL, maxSize = 250) => {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = function () {
        try {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxSize || height > maxSize) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxSize;
              height = maxSize / aspectRatio;
            } else {
              height = maxSize;
              width = maxSize * aspectRatio;
            }
          }
          const scale = 1.25;
          canvas.width = width * scale;
          canvas.height = height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        } catch (error) {
          resolve(imageURL);
        }
      };
      img.onerror = () => {
        resolve(imageURL);
      };
      img.src = imageURL;
    } catch (error) {
      console.error("Unexpected error in fixImageOrientation:", error);
      resolve(imageURL);
    }
  });
};

export const fetchMediaData = async (file, isBlobURL = false) => {
  const url = file?.includes("https")
    ? file
    : process.env.REACT_APP_IMAGE_URL + file;
  const response = await fetch(url);
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  if (isBlobURL) {
    return URL.createObjectURL(blob);
  } else {
    return blob;
  }
};

export const mediaToBase64 = async (media) => {
  return new Promise((resolve, reject) => {
    if (!media || !media?.size) return media;
    const reader = new FileReader();
    reader.readAsDataURL(media);
    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result);
      } else {
        reject(media);
      }
    };
    reader.onerror = () => {
      reject(media);
    };
  });
};

export const mediaURLtoBlob = (base64) => {
  try {
    if (!base64 || !base64.includes("base64,")) return null;
    const [metadata, base64Data] = base64.split(",");
    const mimeMatch = metadata.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  } catch (error) {
    manageErrorLogs(error);
    const blob = dataURLtoBlob(base64);
    return blob || null;
  }
};

export const compressMediaFiles = async (file, type, imgType) => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        try {
          const MAX_DIMENSION = 1024;
          const ratio = Math.min(
            MAX_DIMENSION / img.width,
            MAX_DIMENSION / img.height,
            1
          );

          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          const context = canvas.getContext("2d");
          context.drawImage(img, 0, 0, canvas.width, canvas.height);

          let quality = 0.5;
          if (file.size > 4 * 1024 * 1024) quality = 0.3;
          else if (file.size > 3 * 1024 * 1024) quality = 0.4;
          else if (file.size > 2 * 1024 * 1024) quality = 0.5;
          else quality = 0.5;

          const fileType =
            imgType === "selfie" ? "image/jpeg" : file?.type || "image/png";
          const base64Data = canvas.toDataURL(fileType, quality);
          URL.revokeObjectURL(img?.src);
          resolve(base64Data);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => {
        reject(new Error("Image failed to load"));
      };
    } catch (error) {
      reject(error);
    }
  }).catch(async (error) => {
    manageErrorLogs(error);
    if (type === "file") {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    } else {
      return file;
    }
  });
};

export const nameFormatting = (user, type) => {
  if (type === "FL") {
    return user && user?.firstname?.trim().concat(", ", user?.lastname?.trim());
  } else if (type === "FLC") {
    return user && user?.firstname?.trim().concat(" ", user?.lastname?.trim());
  } else if (type === "LF") {
    return user && user?.lastname?.trim().concat(" ", user?.firstname?.trim());
  } else {
    return user && user?.lastname?.trim().concat(", ", user?.firstname?.trim());
  }
};
export const getDifference = (num1, num2) => {
  if (!Number.isInteger(num1) || !Number.isInteger(num2)) return 0;
  const difference = Math.abs(num1 - num2);
  return difference;
};

export const numberFormatting = (number) => {
  const formattedNumber = new Intl.NumberFormat().format(number && number);
  return formattedNumber;
};

export const modifyText = (data) => {
  return data ? data?.trim()?.toLowerCase() : data;
};

export const getSortData = (data, key) => {
  if (!data || !Array.isArray(data)) return [];
  if (key && data && data?.length > 0) {
    return [...data].sort((a, b) => {
      const valA = a[key]?.trim?.() || "";
      const valB = b[key]?.trim?.() || "";
      return valA.localeCompare(valB);
    });
  } else {
    return data;
  }
};

export const getSeconds = (data) => {
  let mSeconds = data > 0 ? data : 0;
  let seconds = Math.round(mSeconds / 1000);
  return `${seconds} ${seconds > 1 ? "seconds" : "second"}`;
};

export const filterUniqueData = (data, type) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  return Array.from(
    new Map(
      data.map((item) => [
        `${item?.reason}|${JSON.stringify(item?.md_list)}|${item?.date}|${
          item?.user_id
        }|${item?.status}|${item?.created_at}`,
        item,
      ])
    ).values()
  );
};

export const convertDataAsParse = (data) => {
  let newData = null;
  if (!data) return newData;
  if (typeof data === "string") {
    newData = JSON.parse(data);
  } else {
    newData = data;
  }
  return newData;
};

export const checkCatchMessage = (message) => {
  if (
    message &&
    !modifyText(message).includes(modifyText("Transaction aborted"))
  ) {
    notify(message);
  }
};

export const manageErrorLogs = (error) => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (typeof Bugsnag !== "undefined" && Bugsnag.notify) {
      const message =
        typeof error?.message === "string"
          ? error.message
          : typeof error === "string"
          ? error
          : "Unknown error";

      const errorToSend = new Error(message);
      if (error?.name) {
        errorToSend.name = error.name;
      }

      Bugsnag.notify(errorToSend, (event) => {
        if (user && user?.user_id) {
          event.setUser(user.user_id || null, nameFormatting(user) || null);
        }
        if (typeof error === "object") {
          event.addMetadata("errorDetails", {
            ...error,
            name: error.name,
            message: error.message,
            stack: error.stack,
          });
        }
      });
    }
    console.log("APP CATCH ERROR====", error);
  } catch (err) {
    console.log("error", err);
  }
};

// export const getAppLogo = (config, type) => {
//   if (type === "header") {
//     if (config?.name === companyNames.cydc) {
//       return CYDCHeaderLogo;
//     } else if (config?.name === companyNames.cdci) {
//       return CDCIHeaderLogo;
//     } else if (config?.name === companyNames.ajanta) {
//       return AjantaHeaderLogo;
//     } else {
//       return CYDCSHeaderLogo;
//     }
//   } else {
//     if (config?.name === companyNames.cydc) {
//       return CYDCLogo;
//     } else if (config?.name === companyNames.cdci) {
//       return CDCILogo;
//     } else if (config?.name === companyNames.ajanta) {
//       return AjantaLogo;
//     } else {
//       return CYDCSLogo;
//     }
//   }
// };

export const checkIsEnable = (page) => {
  try {
    const currCom = modifyText(process.env.REACT_APP_NAME);
    const currCom2 = `${currCom}_${modifyText(process.env.REACT_APP_SERVER)}`;

    const getComDetails = companyConfig[currCom2]
      ? companyConfig[currCom2]
      : companyConfig[currCom];
    if (!getComDetails) return true;

    if (page) {
      if (!Array.isArray(getComDetails?.pages)) return true;
      return getComDetails?.pages?.includes(page);
    } else {
      return getComDetails;
    }
  } catch (error) {
    return true;
  }
};
